import { GradientParser } from '../../lib/gradient-parser';
import { hashStr } from '../../common-utils';

export const emptyImg = document.createElement('img');
emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export function createImageCacheKey(node) {
  return `${node.id}:${hashStr(node.src || '')}:${node.fill || ''}`;
}

export function disposeImage(renderContext, node, src, { cacheKey, key = '_img' } = {}) {
  const imgCache = renderContext.cache?.loadImage;
  if (!imgCache) return;
  cacheKey = cacheKey || node.id;
  const cache = imgCache.get(node.id);
  node[key] = null;
  if (cache) {
    if (cache.dispose) {
      cache.dispose();
    }
    delete imgCache[cacheKey];
  }
}

function noop() {}

/**
 * Check if a blob/source is an SVG image.
 * This handles cases where blob.type may not be correctly set for data URLs.
 */
function isSvgSource(blob, originalSrc) {
  // Primary check: blob MIME type
  if (blob.type === 'image/svg+xml') {
    return true;
  }
  // Fallback: check if original src is SVG data URL or SVG file
  if (typeof originalSrc === 'string') {
    if (originalSrc.startsWith('data:image/svg+xml')) {
      return true;
    }
    const lowercaseSrc = originalSrc.toLowerCase();
    if (lowercaseSrc.endsWith('.svg') || lowercaseSrc.includes('.svg?')) {
      return true;
    }
  }
  return false;
}

/**
 * Normalize color to hex format for SVG compatibility.
 * Some browsers have issues parsing rgb(0, 0, 0) in SVG fill attributes.
 * Converting to hex format (#RRGGBB) ensures consistent rendering.
 */
function normalizeColorForSvg(color) {
  if (!color || typeof color !== 'string') return color;

  // Match rgb() or rgba() format
  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }

  return color;
}

export function loadImage(renderContext, src, callback, node) {
  if (!src)
    return {
      img: null,
    };

  const imgCache = renderContext.cache.loadImage;
  const cacheKey = createImageCacheKey(node);
  const cache = imgCache.get(node.id);

  if (cache) {
    if (cache.cacheKey === cacheKey) {
      if (cache.loading) {
        cache.ready.then(callback || noop);
        return cache;
      } else {
        return cache;
      }
    } else {
      disposeImage(renderContext, node, { cacheKey: cacheKey });
    }
  }

  const img = new Image();

  const cacheItem = {
    cacheKey,
    src: src,
    loading: true,
    error: null,
    img: null,
  };

  // Store original src before asset provider transformation for SVG detection
  const originalSrc = src;

  imgCache.set(node.id, cacheItem);
  const job = Promise.resolve(renderContext.providers.asset.getAsset(src))
    .then(async (src) => {
      const res = await fetch(src, { mode: 'cors' });
      let blob = await res.blob();

      // Determine if this is an SVG (handles data URL type detection issues)
      const isSvg = isSvgSource(blob, originalSrc);

      // Clean up SVG dimensions if it's an SVG file
      if (isSvg) {
        const text = await blob.text();
        // Replace problematic width/height percentage attributes with fixed values in <svg> tag only
        let cleanedSvg = text.replace(
          /<svg([^>]*)\s+width\s*=\s*["']?100%["']?([^>]*)>/gi,
          '<svg$1 width="800"$2>'
        );
        cleanedSvg = cleanedSvg.replace(
          /<svg([^>]*)\s+height\s*=\s*["']?100%["']?([^>]*)>/gi,
          '<svg$1 height="600"$2>'
        );

        // Create a new blob with cleaned SVG content
        const cleanedBlob = new Blob([cleanedSvg], { type: 'image/svg+xml' });
        blob = cleanedBlob;
      }

      const colorOverrides = node?.colorOverrides;
      if (colorOverrides && isSvg) {
        const text = await blob.text();
        cacheItem.colors = getSvgColors(text);
        cacheItem.svgContent = text;
        if (colorOverrides) {
          // Apply color overrides to SVG content
          // Normalize colors to hex format for better SVG compatibility
          let modifiedSvg = text;
          Object.entries(colorOverrides).forEach(([originalColor, newColor]) => {
            const normalizedColor = normalizeColorForSvg(newColor);
            const regex = new RegExp(`"${originalColor}"`, 'g');
            modifiedSvg = modifiedSvg.replace(regex, `"${normalizedColor}"`);
          });
          const blob = new Blob([modifiedSvg], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          cacheItem.dispose = () => {
            URL.revokeObjectURL(url);
          };
          return url;
        }
      } else if (node.fill && isSvg) {
        const text = await blob.text();
        cacheItem.colors = getSvgColors(text);
        cacheItem.svgContent = text;
        let modifiedSvg = text;

        // If fill is a CSS gradient, inject SVG gradient defs and reference it
        if (GradientParser && GradientParser.is && GradientParser.is(node.fill)) {
          const gradientId = `genGrad_${hashStr(String(node.fill))}`;
          const gradientDef = createSvgGradientDef(node.fill, gradientId);
          if (gradientDef) {
            modifiedSvg = injectDefsIntoSvg(modifiedSvg, gradientDef);
            // Replace currentColor references with the generated gradient URL (case-insensitive)
            modifiedSvg = modifiedSvg.replace(/currentColor/gi, `url(#${gradientId})`);
          }
        } else {
          // Solid color fallback: replace currentColor with concrete color value
          // Normalize to hex format for better SVG compatibility (some browsers have issues with rgb(0,0,0))
          // Use case-insensitive matching for currentColor
          const fillColor = normalizeColorForSvg(node.fill);
          modifiedSvg = modifiedSvg.replace(/currentColor/gi, fillColor);
        }
        const newBlob = new Blob([modifiedSvg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(newBlob);
        cacheItem.dispose = () => {
          URL.revokeObjectURL(url);
        };
        return url;
      }

      const url = URL.createObjectURL(blob);
      cacheItem.dispose = () => {
        URL.revokeObjectURL(url);
      };
      return url;
    })
    .then((url) => {
      return new Promise((resolve) => {
        img.onload = () => {
          resolve(img);
        };
        img.onerror = (e) => {
          console.error(e, src);
          cacheItem.error = e;
          resolve(null);
        };
        img.src = url;
      });
    })
    .then((img) => {
      cacheItem.img = img;
      cacheItem.loading = false;
      if (callback) callback(img);
      renderContext.api.markNeedRender();
      return img;
    })
    .catch((err) => {
      console.error(err);
      cacheItem.error = `${err.message}`;
      cacheItem.loading = false;
    });

  cacheItem.ready = job;

  return cacheItem;
}

function getSvgColors(svgContent) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svgElement = doc.documentElement;

  // Extract colors from fill and stroke attributes
  const colors = new Set();
  const elements = svgElement.querySelectorAll('*');

  elements.forEach((el) => {
    const fill = el.getAttribute('fill');
    const stroke = el.getAttribute('stroke');

    if (fill && fill !== 'none' && !fill.startsWith('url(')) {
      colors.add(fill);
    }
    if (stroke && stroke !== 'none' && !stroke.startsWith('url(')) {
      colors.add(stroke);
    }
  });

  // Store colors in cache item
  return Array.from(colors);
}

// Create an SVG gradient definition string from a CSS gradient string
function createSvgGradientDef(cssGradient, id) {
  try {
    const astList = GradientParser.parse(cssGradient);
    if (!astList || !astList.length) return '';

    // Use the last parsed definition (matches canvas implementation order)
    const def = astList[astList.length - 1];

    const stopsToSvg = (stops) => {
      return stops
        .map((stop) => {
          let colorValue = '';
          if (stop.type === 'hex') colorValue = `#${stop.value}`;
          else if (stop.type === 'rgb' || stop.type === 'rgba')
            colorValue = `rgba(${stop.value.join(',')})`;

          // Default length present due to parser; use percentage when possible
          let offsetAttr = '';
          if (stop.length) {
            if (stop.length.type === '%') {
              offsetAttr = ` offset="${Math.max(0, Math.min(100, parseFloat(stop.length.value)))}%"`;
            } else if (stop.length.type === 'px') {
              // Approximate px by percentage when using objectBoundingBox
              // Fall back to distributing evenly is already handled by parser; omit offset to let renderer decide
              offsetAttr = '';
            } else if (typeof stop.length.value === 'string') {
              // position-keyword or others; omit for safety
              offsetAttr = '';
            }
          }
          return `<stop${offsetAttr} stop-color="${colorValue}"/>`;
        })
        .join('');
    };

    if (def.type === 'linear-gradient' || def.type === 'repeating-linear-gradient') {
      // Compute x1,y1,x2,y2 from angle, default to left->right
      let x1 = 0,
        y1 = 0.5,
        x2 = 1,
        y2 = 0.5; // objectBoundingBox units
      const orientation = def.orientation;
      const angleInfo = GradientParser.prepareCssAngle(orientation || 'to right');
      const theta = angleInfo?.theta ?? 0; // Canvas-based; map to unit vector
      // Convert to vector on unit box edges using getRectAngleBound-like approach simplified for bbox 1x1
      const bx = Math.cos(theta);
      const by = Math.sin(theta);
      // Start/end on bbox edges projected along direction
      // Use center +/- big vector, then clamp intersect to edges (approximation)
      const cx = 0.5,
        cy = 0.5;
      const scale = 2; // large enough to hit edges
      const sx = cx - bx * scale,
        sy = cy - by * scale;
      const ex = cx + bx * scale,
        ey = cy + by * scale;
      // clamp to [0,1]
      x1 = Math.max(0, Math.min(1, sx));
      y1 = Math.max(0, Math.min(1, sy));
      x2 = Math.max(0, Math.min(1, ex));
      y2 = Math.max(0, Math.min(1, ey));

      const stops = stopsToSvg(def.colorStops);
      return `<linearGradient id="${id}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" gradientUnits="objectBoundingBox">${stops}</linearGradient>`;
    }

    if (def.type === 'radial-gradient' || def.type === 'repeating-radial-gradient') {
      // Default to center radial, radius 50%
      const cx = '50%',
        cy = '50%',
        r = '50%';
      const stops = stopsToSvg(def.colorStops);
      return `<radialGradient id="${id}" cx="${cx}" cy="${cy}" r="${r}" gradientUnits="objectBoundingBox">${stops}</radialGradient>`;
    }

    // Conic gradients not natively supported in SVG gradients; fallback by ignoring
    return '';
  } catch (e) {
    console.error(e);
    return '';
  }
}

// Inject provided <linearGradient>/<radialGradient> element into <defs> of the SVG content
function injectDefsIntoSvg(svgText, defsContent) {
  if (!defsContent) return svgText;

  // If <defs> exists, inject inside it (after opening tag)
  const hasDefs = /<defs[^>]*>/i.test(svgText);
  if (hasDefs) {
    return svgText.replace(/(<defs[^>]*>)/i, `$1${defsContent}`);
  }

  // Else, insert new <defs> right after <svg ...>
  return svgText.replace(/(<svg[^>]*>)/i, `$1<defs>${defsContent}</defs>`);
}
